import React from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';

import { ProfilePhotoUrls } from '@/types';

interface User {
  id: string;
  name: string;
  nickname?: string;
  profile_photo_urls?: ProfilePhotoUrls | null;
  followers_count?: number;
  is_followed: boolean;
  is_me?: boolean;
}

interface UserListItemProps {
  user: User;
  showFollowButton?: boolean;
  onFollowToggle?: (userId: string) => void;
  followLoading?: boolean;
  context?: 'followers' | 'following' | 'default';
}

const UserListItem: React.FC<UserListItemProps> = ({
  user,
  showFollowButton = true,
  onFollowToggle,
  followLoading = false,
  context = 'default'
}) => {
  const navigate = useNavigate();

  const handleAvatarClick = () => {
    navigate(`/profile/${user.id}`);
  };

  const handleFollowClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking follow button
    onFollowToggle?.(user.id);
  };

  // Get button text based on context and follow state
  const getButtonText = () => {
    if (followLoading) {
      return user.is_followed ? 'Unfollowing...' : 'Following...';
    }

    if (context === 'followers') {
      return user.is_followed ? 'Unfollow' : 'Follow Back';
    } else if (context === 'following') {
      return user.is_followed ? 'Unfollow' : 'Follow';
    } else {
      return user.is_followed ? 'Following' : 'Follow';
    }
  };

  return (
    <button 
      onClick={handleAvatarClick}
      className="flex w-full justify-between items-center"
      style={{ 
        background: 'transparent',
        color: '#FFF',
        fontFamily: '"Aeonik TRIAL"',
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '-0.2px',
        border: 'none',
        borderRadius: '16px',
        marginBottom: '7px'
      }}
    >
      <div className="flex items-center">
        <div style={{ marginRight: '10px' }}>
          <Avatar
            name={user.name}
            profilePhotoUrls={user.profile_photo_urls}
            size="medium"
            style={{ width: '48px', height: '48px' }}
          />
        </div>
        <div className="text-left">
          {/* Nickname goes first */}
          {user.nickname && (
            <div 
              style={{
                color: 'var(--white, #FFF)',
                fontFamily: '"Aeonik TRIAL"',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20px'
              }}
            >
              @{user.nickname}
            </div>
          )}
          {/* Username goes after */}
          <div 
            style={{
              color: 'var(--white-56, rgba(255, 255, 255, 0.56))',
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '12px'
            }}
          >
            {user.name}
          </div>
        </div>
      </div>
      
      {/* Follow Button */}
      {showFollowButton && !user.is_me && (
        <button
          onClick={handleFollowClick}
          disabled={followLoading}
          className={`font-medium transition-colors ${
            followLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          style={{ 
            padding: '8px 16px',
            color: '#fff',
            border: 0,
            fontSize: '16px',
            lineHeight: '20px',
            borderRadius: '18px',
            minWidth: '80px',
            background: user.is_followed 
              ? 'rgba(255, 255, 255, 0.2)' 
              : 'var(--primary, linear-gradient(94deg, #FF2A2E 0%, #FEB03A 100%))'
          }}
        >
          {getButtonText()}
        </button>
      )}
    </button>
  );
};

export default UserListItem; 